import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  createUser,
  deleteUser,
  downloadUsersExport,
  importUsers,
  listFileBasedUsers,
  listUsers,
  resetPassword,
  setSimultaneousUse,
  setUserEnabled,
} from "@/api/endpoints/users";
import type { ImportResult } from "@/api/endpoints/users";
import { Modal } from "@/components/Modal";
import { useAuthStore } from "@/store/authStore";

function Avatar({ name }: { name: string }) {
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft font-mono text-xs font-semibold text-accent">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

function StatusPill({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
        enabled ? "bg-success/10 text-success" : "bg-critical/10 text-critical"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${enabled ? "bg-success" : "bg-critical"}`} />
      {enabled ? "Enabled" : "Disabled"}
    </span>
  );
}

function PrimaryButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return (
    <button
      {...rest}
      className={`rounded-md bg-accent px-3.5 py-2 text-sm font-medium text-bg transition-opacity hover:opacity-90 disabled:opacity-50 ${className}`}
    />
  );
}

function SecondaryButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return (
    <button
      {...rest}
      className={`rounded-md border border-border bg-surface-2 px-3.5 py-2 text-sm text-text transition-colors hover:border-accent disabled:opacity-50 ${className}`}
    />
  );
}

function EmptyRow({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12 text-center text-sm text-text-muted">
        {children}
      </td>
    </tr>
  );
}

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [simultaneousUse, setSimultaneousUseValue] = useState(2);

  const mutation = useMutation({
    mutationFn: () => createUser(username, password, simultaneousUse),
    onSuccess: () => {
      toast.success(`Created ${username}`);
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Modal title="Create user" onClose={onClose}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <label className="flex flex-col gap-1 text-sm text-text-muted">
          Username
          <input
            required
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="rounded-md border border-border bg-surface-2 px-3 py-2 text-text outline-none focus:border-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-text-muted">
          Password
          <input
            required
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-md border border-border bg-surface-2 px-3 py-2 font-mono text-text outline-none focus:border-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-text-muted">
          Simultaneous-Use
          <input
            type="number"
            min={0}
            max={100}
            value={simultaneousUse}
            onChange={(e) => setSimultaneousUseValue(Number(e.target.value))}
            className="rounded-md border border-border bg-surface-2 px-3 py-2 text-text outline-none focus:border-accent"
          />
        </label>
        <PrimaryButton type="submit" disabled={mutation.isPending} className="mt-2">
          Create user
        </PrimaryButton>
      </form>
    </Modal>
  );
}

function ResetPasswordModal({ username, onClose }: { username: string; onClose: () => void }) {
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    mutationFn: () => resetPassword(username, password),
    onSuccess: () => {
      toast.success(`Password updated for ${username}`);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Modal title={`Reset password — ${username}`} onClose={onClose}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <label className="flex flex-col gap-1 text-sm text-text-muted">
          New password
          <input
            required
            autoFocus
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-md border border-border bg-surface-2 px-3 py-2 font-mono text-text outline-none focus:border-accent"
          />
        </label>
        <PrimaryButton type="submit" disabled={mutation.isPending} className="mt-2">
          Update password
        </PrimaryButton>
      </form>
    </Modal>
  );
}

type SourceTab = "sql" | "file";

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative px-4 py-3 text-sm font-medium transition-colors ${
        active ? "text-text" : "text-text-muted hover:text-text"
      }`}
    >
      {children}
      {active && <span className="absolute inset-x-4 -bottom-px h-0.5 rounded-full bg-accent" />}
    </button>
  );
}

export function UsersPage() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManage = hasPermission("users.manage");
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<SourceTab>("sql");
  const [search, setSearch] = useState("");
  const [fileSearch, setFileSearch] = useState("");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const usersQuery = useQuery({
    queryKey: ["users", search, page],
    queryFn: () => listUsers(search, page),
  });
  const fileUsersQuery = useQuery({ queryKey: ["users", "file-based"], queryFn: listFileBasedUsers });

  const filteredFileUsers = useMemo(() => {
    const items = fileUsersQuery.data?.items ?? [];
    if (!fileSearch) return items;
    const needle = fileSearch.toLowerCase();
    return items.filter((u) => u.username.toLowerCase().includes(needle));
  }, [fileUsersQuery.data, fileSearch]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["users"] });

  const toggleEnabled = useMutation({
    mutationFn: ({ username, enabled }: { username: string; enabled: boolean }) =>
      setUserEnabled(username, enabled),
    onSuccess: invalidate,
    onError: (err: Error) => toast.error(err.message),
  });

  const removeUser = useMutation({
    mutationFn: (username: string) => deleteUser(username),
    onSuccess: () => {
      toast.success("User deleted");
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateSimUse = useMutation({
    mutationFn: ({ username, value }: { username: string; value: number }) =>
      setSimultaneousUse(username, value),
    onSuccess: invalidate,
    onError: (err: Error) => toast.error(err.message),
  });

  const dryRunImport = useMutation({
    mutationFn: (file: File) => importUsers(file, true),
    onSuccess: setImportPreview,
    onError: (err: Error) => toast.error(err.message),
  });

  const commitImport = useMutation({
    mutationFn: (file: File) => importUsers(file, false),
    onSuccess: (result) => {
      toast.success(`Imported ${result.created} user(s), skipped ${result.skipped}`);
      setImportPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const totalPages = usersQuery.data
    ? Math.max(1, Math.ceil(usersQuery.data.total / usersQuery.data.page_size))
    : 1;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-border bg-surface">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h1 className="text-lg font-semibold text-text">Users</h1>
            <p className="text-sm text-text-muted">RADIUS accounts across both live sources.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-2">
          <TabButton active={tab === "sql"} onClick={() => setTab("sql")}>
            SQL (radcheck) <span className="text-text-muted">· {usersQuery.data?.total ?? "…"}</span>
          </TabButton>
          <TabButton active={tab === "file"} onClick={() => setTab("file")}>
            File-based <span className="text-text-muted">· {fileUsersQuery.data?.total ?? "…"}</span>
          </TabButton>
        </div>

        {tab === "sql" ? (
          <>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 px-5 py-4">
              <input
                placeholder="Search username…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-64 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text outline-none focus:border-accent"
              />
              <div className="ml-auto flex gap-2">
                <SecondaryButton onClick={() => downloadUsersExport().catch((e) => toast.error(e.message))}>
                  Export CSV
                </SecondaryButton>
                {canManage && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) dryRunImport.mutate(file);
                      }}
                    />
                    <SecondaryButton onClick={() => fileInputRef.current?.click()}>
                      Import CSV
                    </SecondaryButton>
                    <PrimaryButton onClick={() => setCreateOpen(true)}>Create user</PrimaryButton>
                  </>
                )}
              </div>
            </div>

            {importPreview && (
              <div className="mx-5 mb-4 rounded-md border border-border bg-surface-2 p-4">
                <p className="mb-2 text-sm text-text">
                  Preview: {importPreview.created} to create, {importPreview.skipped} skipped.
                </p>
                <div className="mb-3 max-h-40 overflow-y-auto font-mono text-xs text-text-muted">
                  {importPreview.rows.map((r, i) => (
                    <div key={i}>
                      {r.username} — {r.action}
                      {r.reason ? ` (${r.reason})` : ""}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <PrimaryButton
                    disabled={commitImport.isPending || importPreview.created === 0}
                    onClick={() => {
                      const file = fileInputRef.current?.files?.[0];
                      if (file) commitImport.mutate(file);
                    }}
                  >
                    Confirm import
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => {
                      setImportPreview(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-text-muted">
                    <th className="px-5 py-2.5">Username</th>
                    <th className="px-5 py-2.5">Status</th>
                    <th className="px-5 py-2.5">Simultaneous-Use</th>
                    {canManage && <th className="px-5 py-2.5">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {usersQuery.data?.items.map((u) => (
                    <tr key={u.username} className="border-b border-border last:border-0 hover:bg-surface-2/60">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={u.username} />
                          <span className="font-mono text-text">{u.username}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <StatusPill enabled={u.enabled} />
                      </td>
                      <td className="px-5 py-3">
                        {canManage ? (
                          <input
                            type="number"
                            min={0}
                            max={100}
                            defaultValue={u.simultaneous_use ?? 0}
                            onBlur={(e) => {
                              const value = Number(e.target.value);
                              if (value !== u.simultaneous_use) {
                                updateSimUse.mutate({ username: u.username, value });
                              }
                            }}
                            className="w-16 rounded border border-border bg-surface-2 px-2 py-1 text-text tabular-nums outline-none focus:border-accent"
                          />
                        ) : (
                          <span className="tabular-nums text-text">{u.simultaneous_use ?? "—"}</span>
                        )}
                      </td>
                      {canManage && (
                        <td className="px-5 py-3">
                          <div className="flex gap-4 text-xs">
                            <button
                              onClick={() => toggleEnabled.mutate({ username: u.username, enabled: !u.enabled })}
                              className="font-medium text-accent hover:underline"
                            >
                              {u.enabled ? "Disable" : "Enable"}
                            </button>
                            <button
                              onClick={() => setResetTarget(u.username)}
                              className="font-medium text-accent hover:underline"
                            >
                              Reset password
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Delete user ${u.username}? This cannot be undone.`)) {
                                  removeUser.mutate(u.username);
                                }
                              }}
                              className="font-medium text-critical hover:underline"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {usersQuery.data?.items.length === 0 && (
                    <EmptyRow colSpan={canManage ? 4 : 3}>No users match "{search}".</EmptyRow>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {usersQuery.data && totalPages > 1 && (
              <div className="flex items-center gap-3 border-t border-border px-5 py-3 text-sm text-text-muted">
                <SecondaryButton
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-2 py-1"
                >
                  Prev
                </SecondaryButton>
                <span>
                  Page {page} of {totalPages} · {usersQuery.data.total} users
                </span>
                <SecondaryButton
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-2 py-1"
                >
                  Next
                </SecondaryButton>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 px-5 py-4">
              <input
                placeholder="Search username…"
                value={fileSearch}
                onChange={(e) => setFileSearch(e.target.value)}
                className="w-64 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text outline-none focus:border-accent"
              />
              <p className="ml-auto max-w-md text-right text-xs text-text-muted">
                From <code className="font-mono">{fileUsersQuery.data?.source_path}</code> — genuinely
                live for authentication, read-only here. Ask if you want write support built.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-text-muted">
                    <th className="px-5 py-2.5">Username</th>
                    <th className="px-5 py-2.5">Simultaneous-Use</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFileUsers.map((u) => (
                    <tr key={u.username} className="border-b border-border last:border-0 hover:bg-surface-2/60">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={u.username} />
                          <span className="font-mono text-text">{u.username}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 tabular-nums text-text">{u.simultaneous_use ?? "—"}</td>
                    </tr>
                  ))}
                  {filteredFileUsers.length === 0 && (
                    <EmptyRow colSpan={2}>No file-based users match "{fileSearch}".</EmptyRow>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {createOpen && <CreateUserModal onClose={() => setCreateOpen(false)} />}
      {resetTarget && <ResetPasswordModal username={resetTarget} onClose={() => setResetTarget(null)} />}
    </div>
  );
}
